import { currencyByCode, } from './currencies.js';
import { BigNumber } from 'bignumber.js';
const NUM_DECIMAL_PLACES = 8;
// Follow "banker's rounding"
BigNumber.config({
    DECIMAL_PLACES: NUM_DECIMAL_PLACES,
    ROUNDING_MODE: BigNumber.ROUND_HALF_EVEN,
});
/**
 * The `Money` type is used to represent a monetary value in a specific currency. It allows for safe arithmetic
 * with 8 decimal places of precision.
 *
 * Amounts are represented in their fractional unit.
 * E.g. USD is represented in dollars.
 *
 * Within the API codebase we should always represent monetary values using the `Money` type. The only
 * exception is when we are serializing/deserializing `Money` values to/from our database, GraphQL API, or a 3rd party API.
 *
 * The Money type is immutable. Calling any function on a Money object will not mutate the data, but instead return a new object.
 */
export class Money {
    currency;
    currencyCode;
    amount;
    constructor(amount, currencyCode) {
        const currency = currencyByCode[currencyCode];
        if (!currency) {
            throw new Error(`Invalid currency code: ${currencyCode}`);
        }
        this.currency = currency;
        this.currencyCode = currencyCode;
        this.amount = new BigNumber(amount);
        if (this.amount.isNaN()) {
            throw new Error(`Invalid money amount: ${amount}`);
        }
    }
    static fromEuros(amountEuros) {
        return new Money(amountEuros, 'EUR');
    }
    static fromUsdDollars(amountDollars) {
        return new Money(amountDollars, 'USD');
    }
    static fromUsdCents(amountCents) {
        return new Money(new BigNumber(amountCents).dividedBy(100), 'USD');
    }
    /**
     * Re-hydrate a serialized Money object.
     *
     * ⚠️ WARNING: Do not manually build the input value for this function as a way to instantiate a Money object.
     * The only input value that should be provided is the output of `Money.serialize()`.
     */
    static deserialize(value) {
        const [currencyCode, amount] = value.split(':');
        if (!amount) {
            throw new Error(`Invalid serialized money value: ${value}`);
        }
        // Note: We can safely cast currencyCode as C since the Money constructor will throw an error if the currency code is invalid.
        return new Money(amount, currencyCode);
    }
    /**
     * Convert a numerical amount + currency from Stripe into a Money object.
     *
     * This method should always be used when converting from Stripe amounts to Money objects unless if we
     * can guarantee that the currency is USD, then we can use `fromUsdCents`.
     *
     * Stripe stores amounts in the smallest unit of the currency (e.g. cents for USD) whereas the Money
     * type stores amounts in their fractional unit (e.g. dollars for USD).
     *
     * Stripe also has some special handling for currencies (See https://stripe.com/docs/currencies#special-cases)
     *
     */
    static fromStripeAmount(amount, currencyCode) {
        const currency = currencyByCode[currencyCode];
        if (!currency) {
            throw new Error(`Invalid currency code: ${currencyCode}`);
        }
        const { customConvertorFromStripeAmount, standardNumberOfDigits } = currency;
        if (customConvertorFromStripeAmount) {
            return new Money(customConvertorFromStripeAmount(amount), currencyCode);
        }
        switch (standardNumberOfDigits) {
            case 0:
                return new Money(new BigNumber(amount), currencyCode);
            case 2:
                return new Money(new BigNumber(amount).dividedBy(100), currencyCode);
            case 3:
                return new Money(new BigNumber(amount).dividedBy(1_000), currencyCode);
            default:
                throw new Error('Unexpected standardNumberOfDigits in fromStripeAmount');
        }
    }
    dividedBy(divisor) {
        if (typeof divisor === 'number' || divisor instanceof BigNumber) {
            return new Money(this.amount.dividedBy(divisor), this.currency.code);
        }
        if (this.currency.code !== divisor.currency.code) {
            throw new Error(`Cannot divide Money amounts if not the same currency! ${this.currency.code} != ${divisor.currency.code}`);
        }
        return new Money(this.amount.dividedBy(divisor.amount), this.currency.code);
    }
    equals(other) {
        if (this.currency.code !== other.currency.code) {
            throw new Error(`Cannot compare Money amounts if not the same currency! ${this.currency.code} != ${other.currency.code}`);
        }
        return this.amount.isEqualTo(other.amount);
    }
    /**
     * Stringified version of the amount including the currency symbol
     * and the standard number of decimals for the currency.
     *
     * E.g.
     *
     *  $1,000.99
     *
     *  €1,000.99
     *
     *  ¥123,456 (0 decimal currency)
     *
     *  KWD 1,234.567 (3 decimal currency)
     *
     */
    format() {
        const currencyFormatter = new Intl.NumberFormat('en', {
            currency: this.currency.code,
            notation: 'standard',
            style: 'currency',
        });
        return currencyFormatter.format(this.amount
            .decimalPlaces(this.currency.standardNumberOfDigits)
            .toNumber());
    }
    /**
     * Compact stringified version of the amount including the currency symbol
     * rounded to an easily readable number
     *
     * E.g.
     *
     *  $100.99 outputs $101
     *
     *  $1500.99 outputs $1.5K
     *
     *  $10,000.99 outputs $10K
     *
     *  €1500.99 outputs €1.5K
     *
     *  ¥123,456 outputs ¥123K (0 decimal currency)
     *
     *  KWD 1,234.567 outputs KWD 1.23K (3 decimal currency)
     */
    formatCompact() {
        const currencyFormatter = new Intl.NumberFormat('en', {
            currency: this.currency.code,
            notation: 'compact',
            style: 'currency',
        });
        return currencyFormatter.format(this.amount
            .decimalPlaces(this.currency.standardNumberOfDigits)
            .toNumber());
    }
    /**
     * Stringified version of the amount including the currency symbol
     * and no decimals.
     *
     * E.g.
     *
     *  $1,000
     *
     *  €1,000
     *
     *  ¥123,456 (0 decimal currency)
     *
     *  KWD 1,234 (3 decimal currency)
     *
     */
    formatRounded() {
        const currencyFormatter = new Intl.NumberFormat('en', {
            currency: this.currency.code,
            maximumFractionDigits: 0,
            notation: 'standard',
            style: 'currency',
        });
        return currencyFormatter.format(this.amount
            .decimalPlaces(this.currency.standardNumberOfDigits)
            .toNumber());
    }
    isGreaterThan(other) {
        if (this.currency.code !== other.currency.code) {
            throw new Error(`Cannot compare Money amounts if not the same currency! ${this.currency.code} != ${other.currency.code}`);
        }
        return this.amount.isGreaterThan(other.amount);
    }
    isGreaterThanOrEqualTo(other) {
        if (this.currency.code !== other.currency.code) {
            throw new Error(`Cannot compare Money amounts if not the same currency! ${this.currency.code} != ${other.currency.code}`);
        }
        return this.amount.isGreaterThanOrEqualTo(other.amount);
    }
    isLessThan(other) {
        if (this.currency.code !== other.currency.code) {
            throw new Error(`Cannot compare Money amounts if not the same currency! ${this.currency.code} != ${other.currency.code}`);
        }
        return this.amount.isLessThan(other.amount);
    }
    isPositive() {
        return this.amount.isGreaterThan(0);
    }
    isZero() {
        return this.amount.isZero();
    }
    minus(other) {
        if (this.currency.code !== other.currency.code) {
            throw new Error(`Cannot minus Money amounts if not the same currency! ${this.currency.code} != ${other.currency.code}`);
        }
        return new Money(this.amount.minus(other.amount), this.currency.code);
    }
    multipliedBy(multiplier) {
        return new Money(this.amount.multipliedBy(multiplier), this.currency.code);
    }
    plus(other) {
        if (this.currency.code !== other.currency.code) {
            throw new Error(`Cannot plus Money amounts if not the same currency! ${this.currency.code} != ${other.currency.code}`);
        }
        return new Money(this.amount.plus(other.amount), this.currency.code);
    }
    /**
     * Rounds the amount to the standard number of decimal places for the currency.
     *
     * Note: The underlying value will still have 8 decimal places of precision, but anything after the standard
     * number of decimal places will be trailing zeroes.
     *
     * E.g.
     *
     * `Money.fromDecimal(100.12999999, 'USD').round(2)`
     *
     *  is equal to
     *
     * `Money.fromUsdDollars(100.13000000)`
     *
     */
    roundToStandardPrecision() {
        return new Money(this.amount.decimalPlaces(this.currency.standardNumberOfDigits), this.currency.code);
    }
    /**
     * Serializes the Money object to a string that can later be deserialized using the `Money.deserialize` method.
     *
     * E.g.
     *
     * $100.99 outputs 'USD:100.99000000'
     *
     * $100.12345678 outputs 'USD:100.12345678'
     *
     * ¥123,456 outputs 'JPY:123456.00000000'
     */
    serialize() {
        return `${this.currency.code}:${this.toFullPrecision()}`;
    }
    /**
     * Stringified version of the amount with 8 decimal places of precision.
     *
     * E.g.
     *
     * $100.12345678 outputs '100.12345678'
     */
    toFullPrecision() {
        return this.amount.toFixed(NUM_DECIMAL_PLACES);
    }
    /**
     * Converts the Money amount into the equivalent Stripe amount. Whenever we call Stripe API with an amount
     * we should use `toStripeAmount()`.
     *
     * Stripe represents amounts in integer values in the smallest currency unit. (e.g. cents for USD) whereas
     * the Money type represents amounts in decimal values in the standard currency unit. (e.g. dollars for USD)
     *
     * Stripe also has some special handling for some currencies  (See https://stripe.com/docs/currencies#special-cases)
     * that this function takes into account.
     */
    toStripeAmount() {
        const { customConvertorToStripeAmount, standardNumberOfDigits } = this.currency;
        // Some currencies have special handling so a custom convertor can be provided
        if (customConvertorToStripeAmount) {
            return customConvertorToStripeAmount(this.amount);
        }
        switch (standardNumberOfDigits) {
            case 0:
                return this.amount.toNumber();
            case 2:
                return this.amount.multipliedBy(100).decimalPlaces(0).toNumber();
            case 3:
                // For 3 decimal currencies Stripe requires them to be rounded to the nearest ten (See https://stripe.com/docs/currencies#three-decimal)
                return this.amount
                    .multipliedBy(100)
                    .decimalPlaces(0)
                    .multipliedBy(10)
                    .toNumber();
            default:
                throw new Error('Unexpected standardNumberOfDigits in toStripeAmount');
        }
    }
    /**
     * Converts the Money amount into the equivalent Stripe amount necessary for a Payout. This method
     * should always be used when creating a Payout.
     *
     * In most cases this is the same as `toStripeAmount()` but some currencies have special handling
     * for Payouts (See https://stripe.com/docs/currencies#special-cases)
     *
     */
    toStripePayoutAmount() {
        const { customConvertorToStripePayoutAmount } = this.currency;
        if (customConvertorToStripePayoutAmount) {
            return customConvertorToStripePayoutAmount(this.amount);
        }
        return this.toStripeAmount();
    }
    /**
     * Converts the amount to a JavaScript Number. This is marked as unsafe because it can lead to loss of precision
     * with very large numbers.
     *
     * Note: This method should be used very rarely.
     *
     * E.g.
     *
     * $100.99 outputs 100.99
     *
     * $100.12345678 outputs 100.12345678
     */
    toUnsafeNumber() {
        return this.amount.toNumber();
    }
    /**
     *
     * @param targetCurrencyCode The currency code to convert to
     * @param exchangeRateCurrentToTarget The exchange rate of converting the current currency to the `target` currency. E.g. If the current object is `Money<'USD'>` and the target currency is `'EUR'` then this should be the USD/EUR exchange rate.
     */
    convertCurrency(targetCurrencyCode, exchangeRateCurrentToTarget) {
        return new Money(this.amount.multipliedBy(exchangeRateCurrentToTarget), targetCurrencyCode);
    }
    /**
     * Converts the amount to a JavaScript Number representing the amount in the smallest unit for the currency rounded to a whole number.
     * For most currencies this is cents. This is marked as unsafe because it can lead to loss of precision
     * with very large numbers. Rounding is done using "banker's rounding".
     *
     * Note: This method should be used very rarely. Don't use this for Stripe API calls, instead use toStripeAmount().
     *
     * E.g.
     *
     * $100.99 USD outputs 10099
     *
     * $100.99 CAD outputs 10099
     *
     * $100.12345678 USD outputs 100012
     *
     * $100 CLP (0 decimal currency) outputs 100
     */
    toUnsafeSmallestUnit() {
        const { standardNumberOfDigits } = currencyByCode[this.currency.code];
        switch (standardNumberOfDigits) {
            case 0:
                return this.amount.decimalPlaces(0).toNumber();
            case 2:
                return this.amount.multipliedBy(100).decimalPlaces(0).toNumber();
            case 3:
                return this.amount.multipliedBy(1_000).decimalPlaces(0).toNumber();
            default:
                throw new Error('Unexpected standardNumberOfDigits in toUnsafeSmallestUnit');
        }
    }
    /**
     * Stringified version of the amount with the standard number of decimal places for the currency.
     * Rounding is done using "banker's rounding".
     *
     * E.g.
     *
     * $100.12345678 outputs '100.12'
     *
     * ¥123 outputs '123' (0 decimal currency)
     *
     * BWD 100.12345678 outputs '100.123' (3 decimal currency)
     */
    toStandardPrecision() {
        return this.amount.toFixed(this.currency.standardNumberOfDigits);
    }
    /**
     *
     * Improves the output of `console.log` and `util.inspect` for Money objects
     * for debugging purposes.
     */
    [Symbol.for('nodejs.util.inspect.custom')](_depth, options) {
        return `Money {
  amount: ${options.stylize("'" + this.toFullPrecision() + "'", 'string')},
  currencyCode: ${options.stylize("'" + this.currencyCode + "'", 'string')},
}`;
    }
}
//# sourceMappingURL=money.js.map